import { installmentScheduleTypeConstants } from "constants/account";
import { validationConstants } from "constants/formValidation";
import { orderTypeConstants } from "constants/order";
import { distributionLogicValueList } from "constants/paymentPlanConstants";
import dayjs from "dayjs";
import { centsToDollars, dollarsToCents, parsePrice } from "helpers/price";
import {
  IDefaultProcedure,
  IPatientResponsibilityProcedure,
} from "types/Order/Order";

import {
  PaymentPlanMode,
  DownPaymentType,
  IValidationResult,
  IPreviewCalculations,
  IPaymentPlanData,
  IInstallmentItem,
} from "./types";

export function groupByCodeDescriptionAndCount(items: any[]): {
  code: number;
  cptDescription: string;
  providers: { name: string; specialty: string; quantity: number }[];
}[] {
  const uniqueWithCount = items.reduce(
    (
      acc: IDefaultProcedure[] | IPatientResponsibilityProcedure[],
      currentItem
    ) => {
      if (
        "procedureId" in currentItem &&
        "providerProcedureId" in currentItem
      ) {
        const { code, procedureId, providerProcedureId } = currentItem;
        const key = `${code}-${procedureId}-${providerProcedureId}`;
        const existingObj = acc.find((obj: any) => Object.keys(obj)[0] === key);
        if (existingObj) {
          (existingObj as any)[key] += 1;
        } else {
          acc.push({ [key]: 1 } as any);
        }
      }
      return acc;
    },
    []
  );

  const uniqueObjectsMap = new Map();
  uniqueWithCount.forEach((obj) => {
    const [key, value] = Object.entries(obj)[0];
    uniqueObjectsMap.set(key, value);
  });

  const grouped = items.reduce(
    (
      acc: {
        code: number;
        cptDescription: string;
        providers: { name: string; specialty: string; quantity: number }[];
      }[],
      currentItem
    ) => {
      const { code, cptDescription } = currentItem;

      let foundItem = acc.find(
        (item) => item.code === code && item.cptDescription === cptDescription
      );

      if (!foundItem) {
        foundItem = { code, cptDescription, providers: [] };
        acc.push(foundItem);
      }

      if (
        "providerName" in currentItem &&
        "specialty" in currentItem &&
        "procedureId" in currentItem &&
        "providerProcedureId" in currentItem
      ) {
        const { providerName, specialty, procedureId, providerProcedureId } =
          currentItem;

        if (
          specialty &&
          providerName &&
          !foundItem.providers.find(
            (currentProvider) => currentProvider.name === providerName
          )
        ) {
          const key = `${code}-${procedureId}-${providerProcedureId}`;
          const quantity: number = uniqueObjectsMap.get(key) || 0;

          foundItem.providers.push({ name: providerName, specialty, quantity });
        }
      }

      return acc;
    },
    []
  );

  return grouped;
}

export function getAbnReason(isMedicare: boolean) {
  return isMedicare
    ? `Medicare does not consider the procedure(s) listed above medically necessary for your condition or deems them experimental.`
    : `Your insurance does not consider the procedure(s) listed above medically necessary for your condition or deems them experimental.`;
}

// Validation function
export function validatePaymentPlanForm(
  editorMode: PaymentPlanMode,
  numberOfMonths: number | null,
  downPaymentValue: number | null,
  downPaymentType: DownPaymentType,
  estimatedCost: number
): IValidationResult {
  const isDownPaymentOnly = editorMode === "downpayment";

  // Duration validation - required only if not downpayment only
  const isDurationValid = isDownPaymentOnly
    ? true
    : numberOfMonths !== null && numberOfMonths >= 1 && numberOfMonths <= 50;

  // Down payment value validation - always required
  const isDownPaymentValid = downPaymentValue !== null && downPaymentValue > 0;

  // Check if it's a whole number for duration
  const isDurationWholeNumber = isDownPaymentOnly
    ? true
    : numberOfMonths === null || Number.isInteger(numberOfMonths);

  // Flat amount validation - for regular payment plans, flat amount must be less than total
  // For downpayment only, this validation doesn't apply
  const isFlatAmountValid =
    isDownPaymentOnly ||
    downPaymentType !== "$" ||
    !downPaymentValue ||
    downPaymentValue < estimatedCost;

  // Calculate duration error message
  let durationError: string | null = null;
  if (!isDurationValid) {
    durationError = validationConstants.PAYMENT_PLAN.DURATION_REQUIRED;
  } else if (!isDurationWholeNumber) {
    durationError = validationConstants.PAYMENT_PLAN.DURATION_WHOLE_NUMBER;
  }

  // Calculate down payment error message
  let downPaymentError: string | null = null;
  if (!isDownPaymentValid) {
    downPaymentError =
      downPaymentType === "%"
        ? validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_PERCENT
        : validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_FLAT;
  } else if (!isFlatAmountValid) {
    downPaymentError = `Amount must be less than ${parsePrice(estimatedCost)}`;
  }

  return {
    isDurationValid,
    isDownPaymentValid,
    isDurationWholeNumber,
    isFlatAmountValid,
    isFormValid:
      isDurationValid &&
      isDownPaymentValid &&
      isDurationWholeNumber &&
      isFlatAmountValid,
    errors: {
      duration: durationError,
      downPayment: downPaymentError,
    },
  };
}

// Calculate preview values based on editor inputs
export function calculatePreview(
  estimatedCost: number,
  downPaymentType: DownPaymentType,
  downPaymentValue: number | null,
  numberOfMonths: number | null,
  editorMode: PaymentPlanMode
): IPreviewCalculations {
  let calculatedDownPayment = 0;

  if (downPaymentType === "%" && downPaymentValue) {
    calculatedDownPayment = estimatedCost * (downPaymentValue / 100);
  } else if (downPaymentType === "$" && downPaymentValue) {
    calculatedDownPayment = downPaymentValue;
  }

  const isDownPaymentOnly = editorMode === "downpayment";
  const remainingAmount = estimatedCost - calculatedDownPayment;
  const months = numberOfMonths || 0;
  const monthlyPayment =
    !isDownPaymentOnly && months > 0
      ? Math.floor((remainingAmount / months) * 100) / 100
      : 0;

  return {
    downPayment: calculatedDownPayment,
    monthlyPayment,
    remainingAmount,
    totalPayments: isDownPaymentOnly ? 0 : months,
    isDownPaymentOnly,
  };
}

// Build payment plan data for API submission
export function buildPaymentPlanData(
  editorMode: PaymentPlanMode,
  numberOfMonths: number | null,
  downPaymentType: DownPaymentType,
  downPaymentValue: number | null,
  providerDistributionLogic?: distributionLogicValueList
): IPaymentPlanData {
  const isDownPaymentOnly = editorMode === "downpayment";

  return {
    remainingAmountInCents: null,
    paymentPlanDuration: isDownPaymentOnly ? 1 : numberOfMonths || 1,
    paymentPlanMinDownPaymentPercent:
      downPaymentType === "%" && downPaymentValue ? downPaymentValue : null,
    paymentPlanMinDownPaymentFlat:
      downPaymentType === "$" && downPaymentValue
        ? dollarsToCents(downPaymentValue)
        : null,
    active: true,
    hasInstallments: false,
    isDownPaymentOnly,
    providerDistributionLogic:
      providerDistributionLogic || distributionLogicValueList.BY_PRIORITY,
    differenceByProvider: {},
    valuesByProvider: {},
  };
}

// Get estimated cost based on order type
export function getEstimatedCost(
  orderType: string,
  clearPrice: number,
  fullPrice: number
): number {
  const orderTypeAmountMap = new Map<string, number>([
    [orderTypeConstants.BARIATRICS, clearPrice],
    [orderTypeConstants.BUNDLED, clearPrice],
    [orderTypeConstants.PATIENT_RESPONSIBILITY, fullPrice],
    [orderTypeConstants.GFE, fullPrice],
    [orderTypeConstants.ED, clearPrice],
  ]);

  return orderTypeAmountMap.get(orderType) || 0;
}

// Calculate initial payment from active payment plan
export function calculateInitialPayment(
  activePaymentPlan: any,
  estimatedCost: number
): number {
  if (!activePaymentPlan) return 0;

  const percentage = activePaymentPlan.paymentPlanMinDownPaymentPercent;
  const flatAmount = activePaymentPlan.paymentPlanMinDownPaymentFlat;

  if (flatAmount) {
    return centsToDollars(flatAmount);
  }

  return estimatedCost * (percentage / 100);
}

// Calculate monthly payment
export function calculateMonthlyPayment(
  estimatedCost: number,
  initialPayment: number,
  paymentPlanDuration: number
): number {
  if (!paymentPlanDuration) return 0;

  const remainingAmount = estimatedCost - initialPayment;
  return Math.floor((remainingAmount / paymentPlanDuration) * 100) / 100;
}

export function addMonthsLikeBackend(date: Date, monthsToAdd: number) {
  const d = new Date(date.getTime());
  // Mimic backend behavior: native Date month overflow (Jan 30 + 1 month => Mar 2 in 2026)
  d.setMonth(d.getMonth() + monthsToAdd);
  return d;
}

// Generate installments list for display
export function generateInstallments(
  orderInfo: any,
  initialPayment: number,
  estimatedCost: number,
  paymentPlanDuration: number
): IInstallmentItem[] {
  if (!paymentPlanDuration) return [];

  const remainingAmount = estimatedCost - initialPayment;
  const basePayment =
    Math.floor((remainingAmount / paymentPlanDuration) * 100) / 100;
  const lastPayment = remainingAmount - basePayment * (paymentPlanDuration - 1);

  const { installmentScheduleType } = orderInfo.account;
  const isTwoWeekSchedule =
    installmentScheduleType ===
    installmentScheduleTypeConstants.TWO_WEEKS_BEFORE;

  let startDate: dayjs.Dayjs;
  let addAMonth = true;

  if (isTwoWeekSchedule) {
    const twoWeeksBefore = dayjs
      .utc(orderInfo.dateOfService)
      .subtract(14, "days");
    const now = dayjs.utc();

    startDate = twoWeeksBefore.isBefore(now) ? dayjs() : twoWeeksBefore;
    addAMonth = !!twoWeeksBefore.isBefore(now);
  } else {
    startDate = dayjs();
  }
  // Convert startDate to a native Date once, then do backend-style setMonth math
  const startNativeDate = startDate.toDate();

  const installments = Array.from(
    { length: paymentPlanDuration },
    (_, index) => {
      const updatedIndex = addAMonth ? index + 1 : index;
      const dueNative = addMonthsLikeBackend(startNativeDate, updatedIndex);
      const date = dayjs(dueNative).format("YYYY-MM-DD");
      const payment =
        index === paymentPlanDuration - 1 ? lastPayment : basePayment;

      return { date, payment };
    }
  );

  const initialPaymentObj: IInstallmentItem = {
    date: "Due Today",
    payment: initialPayment,
  };

  return [initialPaymentObj, ...installments];
}

// Initialize editor state from existing payment plan
export interface IEditorInitState {
  numberOfMonths: number | null;
  downPaymentType: DownPaymentType;
  downPaymentValue: number | null;
}

export function initializeEditorState(
  activePaymentPlan: any
): IEditorInitState {
  if (activePaymentPlan) {
    // Edit mode - fill with existing values
    const hasFlat = !!activePaymentPlan.paymentPlanMinDownPaymentFlat;

    return {
      numberOfMonths: activePaymentPlan.paymentPlanDuration || null,
      downPaymentType: hasFlat ? "$" : "%",
      downPaymentValue: hasFlat
        ? centsToDollars(activePaymentPlan.paymentPlanMinDownPaymentFlat)
        : activePaymentPlan.paymentPlanMinDownPaymentPercent || null,
    };
  }

  // Create mode - start with empty values
  return {
    numberOfMonths: null,
    downPaymentType: "$",
    downPaymentValue: null,
  };
}
